/**
 * PostBountyModal — tree owner posts a paid bounty on one of their
 * public mysteries. Submits to POST /api/bounties, which returns a
 * Stripe-hosted Checkout URL we redirect the browser to.
 *
 * Min €5, max €500. Quick presets for convenience.
 */
import { useState } from 'react'
import { api } from '../../services/api.js'

const PRESETS = [500, 1000, 2500, 5000, 10000]  // cents
const MIN = 500
const MAX = 50000

export default function PostBountyModal({ mysteryId, onClose }) {
  const [amount, setAmount] = useState(2500)
  const [posting, setPosting] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    if (amount < MIN || amount > MAX) {
      setErr(`Amount must be between €${MIN / 100} and €${MAX / 100}`)
      return
    }
    setPosting(true)
    setErr(null)
    try {
      const r = await api.post('/bounties', {
        mystery_id: mysteryId,
        amount_cents: amount,
        currency: 'eur',
      })
      window.location.href = r.checkout_url
    } catch (e) {
      setErr(e.message || 'Could not post bounty')
      setPosting(false)
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal post-bounty-modal" onClick={e => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Post a bounty</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={submit} className="post-bounty-modal__body">
          <p className="post-bounty-modal__intro">
            Reward another researcher for solving this mystery. You're charged now
            and the platform holds the funds; when a solver's contribution is
            approved, they receive the amount minus a 15% platform fee.
          </p>

          <div className="post-bounty-modal__presets">
            {PRESETS.map(p => (
              <button
                key={p} type="button"
                className={`post-bounty-modal__preset ${amount === p ? 'is-active' : ''}`}
                onClick={() => setAmount(p)}
              >€{p / 100}</button>
            ))}
          </div>

          <label className="post-bounty-modal__custom">
            <span>Custom amount (€)</span>
            <input
              type="number" min={MIN / 100} max={MAX / 100} step="1"
              value={amount / 100}
              onChange={e => setAmount(Math.round(parseFloat(e.target.value || 0) * 100))}
            />
          </label>

          <div className="post-bounty-modal__breakdown">
            <div><span>Your charge</span> <strong>€{(amount / 100).toFixed(2)}</strong></div>
            <div><span>Platform fee (15%)</span> <strong>€{((amount * 0.15) / 100).toFixed(2)}</strong></div>
            <div><span>Solver receives</span> <strong>€{((amount * 0.85) / 100).toFixed(2)}</strong></div>
          </div>

          {err && <div className="post-bounty-modal__err">{err}</div>}

          <div className="post-bounty-modal__actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={posting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={posting}>
              {posting ? 'Opening Stripe…' : `Fund €${(amount / 100).toFixed(2)} →`}
            </button>
          </div>

          <p className="post-bounty-modal__note">
            Bounty expires after 90 days. If no solver is approved by then, you're refunded automatically.
            You can cancel a funded bounty for a refund any time before it pays out.
          </p>
        </form>
      </div>
    </div>
  )
}
